import axios from "axios";
import { getToken } from "./getToken";
import { useDispatch } from "react-redux";
import {
  hideLoader,
  showLoader,
} from "../component-lib/Molecules/loader/LoaderSlice";
import { Dispatch } from "@reduxjs/toolkit";
import { eventEHRCustomToastMessage } from "../component-lib/Atoms/custom-toast-message/EventEHRCustomToastMessage";
/**
 * Generic function to fetch lazy-loading data with pagination.
 * @param {string} path - The API endpoint path.
 * @param {number} pageCount - The page number for pagination.
 * @param {number} pageLimit - The number of records per page.
 */
export const getLazyLoadingApiResponse = async (
  path: string,
  pageCount: number,
  pageLimit: number,
  additionalParams: Record<string, any> = {},
) => {
  return await axios.get(`${process.env.REACT_APP_API_URL}/${path}`, {
    params: {
      pageNumber: pageCount || 1, // Default to 1 if no pageCount is provided
      limit: pageLimit || 10, // Default to 10 if no pageLimit is provided
      ...additionalParams, // Spread additional parameters if provided
    },
    headers: {
      Authorization: `Bearer ${JSON.parse(getToken() || "")}`, // Attach the token
    },
  });
};

/**
 * Generic function to fetch data based on search input.
 * @param {string} path - The API endpoint path.
 * @param {string} search - The search term.
 * @param {string} [searchKey="searchKey"] - The query parameter name for the search input.
 */
export const getLazyLoadingSearchApiResponse = async (
  path: string,
  search: string,
  searchKey: string = "searchKey",
  additionalParams: Record<string, any> = {},
) => {
  return await axios.get(`${process.env.REACT_APP_API_URL}/${path}`, {
    params: {
      [searchKey]: search || undefined, // Default to undefined if no search is provided
      ...additionalParams, // Spread additional parameters if provided
    },
    headers: {
      Authorization: `Bearer ${JSON.parse(getToken() || "")}`, // Attach the token
    },
  });
};

/**
 * Specific function to fetch medicines based on search input.
 * This is kept separate for use cases where the parameter name differs.
 * @param {string} path - The API endpoint path.
 * @param {string} search - The medicine name to search for.
 */
export const getLazyLoadingMedicineSearchApiResponse = async (
  path: string,
  search: string
) => {
  return await axios.get(`${process.env.REACT_APP_API_URL}/${path}`, {
    params: {
      medicineName: search || undefined, // Use "medicineName" as the query parameter
    },
    headers: {
      Authorization: `Bearer ${JSON.parse(getToken() || "")}`, // Attach the token
    },
  });
};

export const postBulkDataImportApiResponse = async (
  data: FormData,
  dispatch: Dispatch
) => {
  try {
    dispatch(showLoader());
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/admin/data-import`,
      data,
      {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${JSON.parse(getToken() || "")}`,
        },
      }
    );
    const message = response?.data?.message || "Data Import Successfully";
    eventEHRCustomToastMessage({
      title: `${message}`,
      status: "success",
    });
    return response.data; // Return the response data
  } catch (error: any) {
    eventEHRCustomToastMessage({
      title: `${error?.response?.data?.message}`,
      status: "error",
    });
    console.error("Error during data import:", error);
    throw error; // Rethrow error to handle it in calling function
  } finally {
    dispatch(hideLoader());
  }
};
